import { useEffect } from "react";
import { Form, Grid, Input } from "antd";
import { EyeInvisibleOutlined, EyeTwoTone } from "@ant-design/icons";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import "./ChangePasswordModal.scss";
import HomesGlobeSmallIcon from "@/assets/brand/HomesGlobeSmallIcon";
import Button from "@/common/atoms/Button";
import { ErrorAlert } from "@/common";
import {
  changePasswordRequest,
  resetErrorMessage,
  userRequest
} from "@/features/user/duck/user";
import AuthHeading from "../common/AuthHeading";
import BrnadInfoAuth from "../common/BrandInfoAuth";
import { PASSWORD_REGEX } from "@/utils";

const ChangePasswordComponent = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();

  const isMobile = !screens.lg;

  const { error, token, userId, userInfo, userError, changePasswordLoading } =
    useSelector(
      (state) => ({
        error: state.auth.error,
        token: state.auth.token,
        userId: state.auth.userId,
        userInfo: state.user.userInfo,
        userError: state.user.error,
        changePasswordLoading: state.user.changePasswordLoading
      }),
      shallowEqual
    );

  useEffect(() => {
    if (!userInfo) {
      dispatch(userRequest({ userId }));
    }
    dispatch(resetErrorMessage());
  }, [token]);

  /**
   * onFinish is the callback when the change password form is submitted.
   * @param {Object} values - The object with the form values.
   * @returns {void} - Returns nothing, but dispatches the changePasswordRequest.
   */
  const onFinish = (values) => {
    dispatch(
      changePasswordRequest({
        ...values,
        id: userInfo?.uuid
      })
    );
    form.resetFields();
  };

  return (
    <div className="flex gap-1 relative change-password-modal">
      <div className="bg-white rounded-lg mt-[40px] p-8 flex-[2] relative change-password-modal overflow-hidden flex flex-col justify-evenly">
        <HomesGlobeSmallIcon />
        <AuthHeading
          title={t("header.changePassword")}
          subTitle={t("changePassword.instruction")}
        />
        {/* FORM */}
        {error || (userError && <ErrorAlert error={error || userError} />)}

        <Form
          form={form}
          name="changePassword"
          onFinish={onFinish}
          layout="vertical">
          {userInfo?.isPasswordSet && (
            <Form.Item
              name="oldPassword"
              label={
                <span className="text-secondary font-poppins">
                  {t("changePassword.currentPassword")}
                </span>
              }
              rules={[
                {
                  required: true,
                  message: t("changePassword.currentPasswordRequired")
                }
              ]}>
              <Input.Password
                placeholder={t("changePassword.enterCurrentPassword")}
                iconRender={(visible) =>
                  visible ? (
                    <EyeTwoTone twoToneColor="#7B9E7E" />
                  ) : (
                    <EyeInvisibleOutlined />
                  )
                }
                className="rounded-md h-12 flex items-center justify-center "
              />
            </Form.Item>
          )}

          <Form.Item
            name="newPassword"
            label={
              <span className="text-secondary font-poppins">
                {t("common.password")}
              </span>
            }
            rules={[
              { required: true, message: t("common.passwordRequired") },
              {
                pattern: PASSWORD_REGEX,
                message: t("changePassword.passwordRequirements")
              }
            ]}>
            <Input.Password
              placeholder={t("common.enterPassword")}
              iconRender={(visible) =>
                visible ? (
                  <EyeTwoTone twoToneColor="#7B9E7E" />
                ) : (
                  <EyeInvisibleOutlined />
                )
              }
              className="rounded-md h-12 flex items-center justify-center "
            />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label={
              <span className="text-secondary font-poppins">
                {t("common.confirmPassword")}
              </span>
            }
            rules={[
              { required: true, message: t("common.confirmPasswordRequired") },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("newPassword") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error(t("common.passwordsDoNotMatch"))
                  );
                }
              })
            ]}>
            <Input.Password
              placeholder={t("common.reEnterPassword")}
              iconRender={(visible) =>
                visible ? (
                  <EyeTwoTone twoToneColor="#7B9E7E" />
                ) : (
                  <EyeInvisibleOutlined />
                )
              }
              className="rounded-md h-12 flex items-center justify-center "
            />
          </Form.Item>

          <Form.Item>
            <Button
              loading={changePasswordLoading}
              htmlType="submit"
              className="w-full my-4 p-4 h-[37px]">
              {t("common.submit")}
            </Button>
          </Form.Item>
        </Form>
        {/* FORM */}
      </div>
      {!isMobile && (
        <div className="flex-[1.5]  h-auto">
          <BrnadInfoAuth
            title={t("brandInfoAuth.title")}
            subTitle1={t("brandInfoAuth.subTitle1")}
            subTitle2={t("brandInfoAuth.subTitle2")}
          />
        </div>
      )}
    </div>
  );
};

export default ChangePasswordComponent;
